import api from '@/services/axiosApi'
import type { PlanAction } from '../types'

/**
 * Available inline task types that map to backend RightyInlineService task types
 */
export type InlineTaskType =
  | 'create_question'
  | 'create_offer'
  | 'generate_code'
  | 'generate_variants'
  | 'generate_personalization'
  | 'generate_sync_values'
  | 'enrich_changes'
  | 'generate_publish_message'
  | 'generate_internal_id'
  | 'generate_segment_combinations'
  | 'question_analysis'
  | 'bootstrap_flow'
  | 'suggest_survey_learning_areas'
  | 'generate_survey_brief'
  | 'generate_survey_brief_streaming'
  | 'generate_page_personalization_brief'
  | 'generate_page_personalization_brief_streaming'
  | 'build_page_personalization_campaign'
  | 'personalize_content'
  | 'generate_conditions'

/**
 * Response types that can be returned from inline operations
 */
export type InlineResultType = 'plan' | 'json' | 'code' | 'raw'

export interface BootstrapFlowValidationError {
  path: string
  code: string
  message: string
  purposeId?: string
}


/**
 * Result from an inline AI operation
 */
export interface InlineResult {
  success: boolean
  type: InlineResultType
  status?: 'ready' | 'generating' | 'failed'
  requestId?: string
  /** For plan type - the plan action to execute */
  plan?: PlanAction
  data?: Record<string, unknown>
  /** For code type - generated JavaScript code */
  code?: string
  /** For raw type or if no structured content found */
  content?: string
  /** Raw response content (always included) */
  rawContent?: string
  /** Error message if success is false */
  error?: string
  errorCode?: string
  validationErrors?: BootstrapFlowValidationError[]
}

/**
 * Request payload for inline operations
 */
export interface InlineRequest {
  /** The task type to execute */
  taskType: InlineTaskType
  /** The user's instruction */
  instruction: string
  /** Optional additional context for the task */
  context?: Record<string, unknown>
  /** Bypass cached async generation results. */
  noCache?: boolean
}

const parseStructuredContent = (content: string): Record<string, unknown> | null => {
  const trimmed = content.trim()
  const fencedMatch = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i)
  const candidate = fencedMatch ? fencedMatch[1].trim() : trimmed

  if (!candidate) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(candidate)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const ASYNC_POLL_INTERVAL_MS = 3000
const ASYNC_MAX_POLL_ATTEMPTS = 180

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

interface InlineErrorPayload {
  error?: string
  errorCode?: string
  validationErrors?: BootstrapFlowValidationError[]
}

const getInlineErrorPayload = (error: unknown): InlineErrorPayload => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return {}
  }

  const response = error.response
  if (!response || typeof response !== 'object' || !('data' in response)) {
    return {}
  }

  const { data } = response
  if (!data || typeof data !== 'object') {
    return {}
  }

  return {
    error: typeof data.error === 'string' ? data.error : undefined,
    errorCode: typeof data.errorCode === 'string' ? data.errorCode : undefined,
    validationErrors: Array.isArray(data.validationErrors)
      ? data.validationErrors.filter(
          (issue): issue is BootstrapFlowValidationError =>
            !!issue &&
            typeof issue === 'object' &&
            typeof issue.path === 'string' &&
            typeof issue.code === 'string' &&
            typeof issue.message === 'string'
        )
      : undefined
  }
}

const getErrorMessage = (error: unknown, fallback: string) => {
  const payload = getInlineErrorPayload(error)
  if (payload.error) return payload.error
  if (error instanceof Error && error.message) return error.message
  return fallback
}

const buildInlineErrorResult = (error: unknown, fallback: string): InlineResult => {
  const payload = getInlineErrorPayload(error)

  return {
    success: false,
    type: 'raw',
    error: getErrorMessage(error, fallback),
    errorCode: payload.errorCode,
    validationErrors: payload.validationErrors
  }
}

const isInlineRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object'

const toInlineResult = (data: unknown): InlineResult => {
  const source = isInlineRecord(data) ? data : {}
  const content = typeof source.content === 'string' ? source.content : undefined
  const validationErrors = Array.isArray(source.validationErrors)
    ? source.validationErrors.filter(
        (issue): issue is BootstrapFlowValidationError =>
          !!issue &&
          typeof issue === 'object' &&
          typeof issue.path === 'string' &&
          typeof issue.code === 'string' &&
          typeof issue.message === 'string'
      )
    : undefined

  const inlineResult: InlineResult = {
    success: source.success === true,
    type:
      source.type === 'plan' ||
      source.type === 'json' ||
      source.type === 'code' ||
      source.type === 'raw'
        ? source.type
        : 'raw',
    status:
      source.status === 'ready' ||
      source.status === 'generating' ||
      source.status === 'failed'
        ? source.status
        : undefined,
    requestId: typeof source.requestId === 'string' ? source.requestId : undefined,
    plan: isInlineRecord(source.plan) ? (source.plan as PlanAction) : undefined,
    data: isInlineRecord(source.data) ? source.data : undefined,
    code: typeof source.code === 'string' ? source.code : undefined,
    content,
    rawContent: typeof source.rawContent === 'string' ? source.rawContent : undefined,
    error: typeof source.error === 'string' ? source.error : undefined,
    errorCode: typeof source.errorCode === 'string' ? source.errorCode : undefined,
    validationErrors
  }

  if (!inlineResult.data && content) {
    inlineResult.data = parseStructuredContent(content) ?? undefined
  }

  return inlineResult
}

/**
 * Composable for inline/headless Righty AI operations.
 *
 * This provides focused, single-shot AI interactions without conversation history.
 * Used for task-specific AI operations like creating questions, generating code, etc.
 *
 * @example
 * ```typescript
 * const { execute, executeStreaming, isGenerating, streamingContent, result } = useRightyInline()
 *
 * // Non-streaming execution
 * const result = await execute({
 *   taskType: 'create_question',
 *   instruction: 'Create a question about their business size',
 *   context: { existingQuestions: ['Industry', 'Role'] }
 * })
 *
 * if (result.success && result.plan) {
 *   // Execute the plan
 *   await executePlan(result.plan)
 * }
 *
 * // Streaming execution
 * await executeStreaming({
 *   taskType: 'generate_code',
 *   instruction: 'Calculate total from quantity and price'
 * })
 *
 * // Watch streamingContent for real-time updates
 * watchEffect(() => {
 *   console.log(streamingContent.value)
 * })
 * ```
 */
export function useRightyInline(options?: { teamPid?: string }) {
  // Only try to get route if no teamPid was provided
  // This allows the composable to work outside component context when teamPid is passed directly
  const route = options?.teamPid ? null : useRoute()
  const teamPid = computed(() => options?.teamPid || (route?.params?.teamPid as string) || '')

  // State
  const isGenerating = ref(false)
  const streamingContent = ref('')
  const result = ref<InlineResult | null>(null)
  const error = ref<string | null>(null)

  /**
   * Execute an inline AI task (non-streaming).
   * Returns when the AI response is complete.
   */
  async function execute(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      // Include this tab's id so any DraftConfigUpdated broadcasts emitted while
      // the inline AI runs can be self-suppressed by this editor — preventing
      // a force-reload that wipes in-progress edits (ENG-1020). Best-effort:
      // configStore is unavailable in non-component contexts (e.g. background
      // jobs that pass options.teamPid directly), so we tolerate failure.
      let tabId: string | null = null
      try {
        tabId = useConfigStore().tabId?.() ?? null
      } catch {
        // configStore not initialized in this context — broadcast falls back to
        // current behavior (no self-suppression). The forbidden-tools guard on
        // the backend still ensures inline AI cannot publish.
      }

      const { data } = await api.post(`/api/${teamPid.value}/righty/inline`, {
        taskType: request.taskType,
        instruction: request.instruction,
        context: request.context || {},
        tabId
      })

      const inlineResult = toInlineResult(data)
      result.value = inlineResult
      return inlineResult
    } catch (err: unknown) {
      const errorResult = buildInlineErrorResult(
        err,
        'Failed to execute inline task'
      )
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  async function executeAsync(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      let tabId: string | null = null
      try {
        tabId = useConfigStore().tabId?.() ?? null
      } catch {
        // configStore not initialized in this context — broadcast falls back to
        // current behavior (no self-suppression). The forbidden-tools guard on
        // the backend still ensures inline AI cannot publish.
      }

      const startGeneration = (noCache = false) =>
        api.post(`/api/${teamPid.value}/righty/inline/async`, {
          taskType: request.taskType,
          instruction: request.instruction,
          context: request.context || {},
          tabId,
          noCache
        })

      const { data } = await startGeneration(request.noCache === true)
      const initialResult = toInlineResult(data)

      if (initialResult.status === 'ready' || initialResult.status === 'failed') {
        if (!initialResult.success) {
          error.value = initialResult.error || 'Unable to generate the activation draft.'
        }
        result.value = initialResult
        return initialResult
      }

      const requestId = initialResult.requestId
      if (!requestId) {
        const errorResult: InlineResult = {
          success: false,
          type: 'raw',
          error: 'Failed to execute inline task'
        }
        error.value = errorResult.error || null
        result.value = errorResult
        return errorResult
      }

      for (let attempt = 0; attempt < ASYNC_MAX_POLL_ATTEMPTS; attempt += 1) {
        await delay(ASYNC_POLL_INTERVAL_MS)

        const { data: pollData } = await api.get(`/api/${teamPid.value}/righty/inline/async/${requestId}`)
        const pollResult = toInlineResult(pollData)

        if (pollResult.status === 'ready') {
          result.value = pollResult
          return pollResult
        }

        if (pollResult.status === 'failed') {
          if (!pollResult.success) {
            error.value = pollResult.error || 'Unable to generate the activation draft.'
          }
          result.value = pollResult
          return pollResult
        }
      }

      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        status: 'generating',
        requestId,
        error:
          'The draft is still being generated. Please try Build Now again in a moment to reconnect to the same request.'
      }
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } catch (err: unknown) {
      const errorResult = buildInlineErrorResult(
        err,
        'Failed to execute inline task'
      )
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Execute an inline AI task with SSE streaming.
   * Updates streamingContent in real-time as the response arrives.
   */
  async function executeStreaming(request: InlineRequest): Promise<InlineResult> {
    if (!teamPid.value || teamPid.value === 'demo') {
      const errorResult: InlineResult = {
        success: false,
        type: 'raw',
        error: 'No team selected'
      }
      result.value = errorResult
      return errorResult
    }

    isGenerating.value = true
    streamingContent.value = ''
    error.value = null

    try {
      // Get XSRF token from cookie (Laravel Sanctum pattern)
      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1]

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream'
      }
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken)
      }

      // Include this tab's id for self-echo suppression on any DraftConfigUpdated
      // broadcasts emitted by tools the inline run invokes (ENG-1020).
      let tabId: string | null = null
      try {
        tabId = useConfigStore().tabId?.() ?? null
      } catch {
        // configStore not initialized; backend forbidden-tools guard still applies.
      }

      const backendHost = import.meta.env.VITE_APP_BACKEND_HOST || ''
      const response = await fetch(`${backendHost}/api/${teamPid.value}/righty/inline/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          taskType: request.taskType,
          instruction: request.instruction,
          context: request.context || {},
          tabId
        }),
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullText = ''
      let lastWasToolResult = false

      if (reader) {
        let buffer = ''

        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse SSE events from buffer
          const events = buffer.split('\n\n')
          buffer = events.pop() || '' // Keep incomplete event in buffer

          for (const event of events) {
            if (!event.trim()) continue

            // Parse event type and data
            const lines = event.split('\n')
            let eventType = ''
            let eventData = ''

            for (const line of lines) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7)
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6)
              }
            }

            if (!eventData) continue

            try {
              const data = JSON.parse(eventData)

              switch (eventType) {
                // OpenRouter streaming text chunks. Each chunk carries the
                // next slice of model output in `data.content`. Tool-result
                // paragraph spacing is handled by the backend, which now
                // emits a `text` event with `"\n\n"` between tool segments.
                case 'text':
                  if (lastWasToolResult && fullText.length > 0) {
                    if (!fullText.endsWith('\n\n')) {
                      fullText += fullText.endsWith('\n') ? '\n' : '\n\n'
                    }
                  }
                  lastWasToolResult = false
                  fullText += data.content || ''
                  streamingContent.value = fullText
                  break

                case 'tool_complete':
                case 'tool_result':
                  lastWasToolResult = true
                  break

                case 'thinking':
                case 'tool_start':
                  // Diagnostic events — no user-visible state change needed.
                  break

                case 'complete':
                case 'stream_end':
                  // Authoritative final text from the backend (if provided).
                  // Some backends send the canonical buffer here; prefer it
                  // over our accumulated chunks when present.
                  if (typeof data.text === 'string' && data.text.length > 0) {
                    fullText = data.text
                    streamingContent.value = fullText
                  }
                  break

                case 'error':
                  error.value = data.message || 'An error occurred'
                  break
              }
            } catch {
              // Ignore parse errors for malformed events
              console.warn('Failed to parse SSE event:', eventData)
            }
          }
        }
      }

      // Parse the final response to extract structured content
      const inlineResult = parseStreamingResponse(fullText)
      result.value = inlineResult
      return inlineResult
    } catch (err: unknown) {
      const errorResult = buildInlineErrorResult(
        err,
        'Failed to execute streaming inline task'
      )
      error.value = errorResult.error || null
      result.value = errorResult
      return errorResult
    } finally {
      isGenerating.value = false
    }
  }

  /**
   * Parse streaming response text to extract structured content (plan, config, json, code)
   */
  function parseStreamingResponse(responseText: string): InlineResult {
    // Check for plan blocks
    const planMatch = responseText.match(/```plan\s*([\s\S]*?)```/)
    if (planMatch) {
      try {
        const planData = JSON.parse(planMatch[1].trim())
        if (planData) {
          return {
            success: true,
            type: 'plan',
            plan: planData as PlanAction,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JSON code blocks
    const jsonMatch = responseText.match(/```json\s*([\s\S]*?)```/)
    if (jsonMatch) {
      try {
        const jsonData = JSON.parse(jsonMatch[1].trim())
        if (jsonData) {
          return {
            success: true,
            type: 'json',
            data: jsonData,
            rawContent: responseText
          }
        }
      } catch {
        // Fall through to other patterns
      }
    }

    // Check for JavaScript code blocks
    const codeMatch = responseText.match(/```(?:javascript|js)\s*([\s\S]*?)```/)
    if (codeMatch) {
      return {
        success: true,
        type: 'code',
        code: codeMatch[1].trim(),
        rawContent: responseText
      }
    }

    // Return raw response if no structured content found
    return {
      success: true,
      type: 'raw',
      content: responseText,
      rawContent: responseText
    }
  }

  /**
   * Reset the composable state
   */
  function reset() {
    isGenerating.value = false
    streamingContent.value = ''
    result.value = null
    error.value = null
  }

  return {
    // State
    isGenerating: readonly(isGenerating),
    streamingContent: readonly(streamingContent),
    result: readonly(result),
    error: readonly(error),

    execute,
    executeAsync,
    executeStreaming,
    reset
  }
}
